<template>
	<div v-if="variant === 'compact'" class="broker-card compact" :class="{ flush: flush }">
		<div class="top">
			<div v-if="props.picture" class="avatar">
				<img
					:src="props.picture"
					alt="Broker Picture"
					:class="{ loaded: isPictureLoaded }"
					@load="isPictureLoaded = true"
					@error="isPictureLoaded = true"
				/>
				<div v-if="!isPictureLoaded" class="avatar-skeleton" />
			</div>
			<div class="info">
				<div class="text">
					<h3 class="name">{{ props.name }}</h3>
					<div class="title">{{ props.title ? $t(props.title) : $t("opportunities.broker_title") }}</div>
				</div>
				<Popover.Root
					v-if="mobile"
					v-model:open="isContactMenuOpen"
					:positioning="{ placement: 'bottom-end', gutter: 4 }"
				>
					<Popover.Trigger as-child>
						<Button class="cta" variant="secondary" size="s" type="button">
							{{ ctaLabel }}
						</Button>
					</Popover.Trigger>
					<Popover.Positioner>
						<Popover.Content class="contact-menu">
							<button v-if="props.email" type="button" class="item" @click="handleEmailContact">
								<AppIcon :name="IconName.MailSmall" :size="16" :stroke-width="1.2" />
								{{ $t("opportunities.emailAction") }}
							</button>
							<button v-if="props.phone" type="button" class="item" @click="handlePhoneContact">
								<AppIcon :name="IconName.Phone" :size="16" :stroke-width="1.2" />
								{{ $t("opportunities.callAction") }}
							</button>
						</Popover.Content>
					</Popover.Positioner>
				</Popover.Root>
				<Button v-else class="cta" variant="secondary" size="s" @click="handleContact">
					{{ ctaLabel }}
				</Button>
			</div>
		</div>

		<div class="rows">
			<button
				v-if="props.email"
				type="button"
				class="row"
				:class="{ copied: copiedField === 'email' }"
				@click="copyValue(props.email, 'email')"
			>
				<AppIcon :name="IconName.MailSmall" :size="16" :stroke-width="1.2" />
				{{ copiedField === "email" ? $t("callToActions.copied") : props.email }}
			</button>
			<button
				v-if="props.phone"
				type="button"
				class="row"
				:class="{ copied: copiedField === 'phone' }"
				@click="copyValue(props.phone, 'phone')"
			>
				<AppIcon :name="IconName.Phone" :size="16" :stroke-width="1.2" />
				{{ copiedField === "phone" ? $t("callToActions.copied") : props.phone }}
			</button>
		</div>
	</div>

	<div v-else class="broker-card">
		<div v-if="props.picture" class="avatar">
			<img
				:src="props.picture"
				alt="Broker Picture"
				:class="{ loaded: isPictureLoaded }"
				@load="isPictureLoaded = true"
				@error="isPictureLoaded = true"
			/>
			<div v-if="!isPictureLoaded" class="avatar-skeleton" />
		</div>

		<div class="text">
			<h3 class="name">{{ props.name }}</h3>
			<div class="title">{{ props.title ? $t(props.title) : $t("opportunities.broker_title") }}</div>

			<div class="contact">
				<div class="item">
					<span class="green-dot" />
					{{ props.phone }}
				</div>
				<div class="item">
					<span class="green-dot" />
					{{ props.email }}
				</div>
			</div>

			<Button class="cta" variant="secondary" size="s" @click="handleContact">
				{{ ctaLabel }}
			</Button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import { Popover } from "@ark-ui/vue"
import Button from "@/components/ui/Button.vue"

const { t } = useI18n()

const isPictureLoaded = ref(false)

const props = defineProps({
	name: String,
	phone: String,
	email: String,
	picture: String,
	hubspotid: String,
	title: String,
	variant: {
		type: String as () => "default" | "compact",
		default: "default",
	},
	flush: {
		type: Boolean,
		default: false,
	},
	mobile: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits<{
	contact: []
}>()

const ctaLabel = computed(() => t("opportunities.buttonCTAOffmarket"))

async function handleContact() {
	emit("contact")

	if (props.email) {
		await navigator.clipboard.writeText(props.email)
	}

	const subject = encodeURIComponent("Prise de contact")
	const body = encodeURIComponent("Opportunité " + props.hubspotid)
	window.location.href = `mailto:${props.email}?subject=${subject}&body=${body}`
}

const isContactMenuOpen = ref(false)

function handleEmailContact() {
	isContactMenuOpen.value = false
	handleContact()
}

function handlePhoneContact() {
	isContactMenuOpen.value = false
	emit("contact")

	if (props.phone) {
		window.location.href = `tel:${props.phone}`
	}
}

const copiedField = ref<"email" | "phone" | null>(null)

async function copyValue(value: string | undefined, field: "email" | "phone") {
	if (!value) return

	await navigator.clipboard.writeText(value)
	copiedField.value = field
	setTimeout(() => {
		copiedField.value = null
	}, 2000)
}
</script>

<style scoped>
.broker-card {
	border: var(--border-width-thin) solid var(--neutral-200);
	padding: var(--spacing-md);
	background: var(--neutral-0);
	border-radius: var(--border-radius-lg);
	max-width: var(--width-broker-card);
	display: flex;
	align-items: center;
	gap: var(--spacing-md);
	box-shadow: var(--shadow-base);

	.avatar {
		position: relative;
		width: var(--width-broker-image);
		height: var(--width-broker-image);
		border-radius: var(--border-radius-full);
		overflow: hidden;
		flex-shrink: 0;
		box-shadow: var(--shadow-md);

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
			object-position: 70% center;
			transform: scale(1.1);
			opacity: 0;
			transition: opacity 0.15s ease;

			&.loaded {
				opacity: 1;
			}
		}
	}

	.text {
		display: flex;
		flex-direction: column;
		min-width: 0;
		font-size: var(--font-size-small);
		line-height: var(--line-height-normal);
		font-family: var(--font-family-primary);
		color: var(--neutral-900);
	}

	.name {
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-medium);
		font-family: var(--font-family-primary);
		margin: 0;
	}

	.title {
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-medium);
		color: var(--neutral-600);
		margin-top: var(--spacing-xs);
	}

	.contact {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		margin-top: var(--spacing-md);

		.item {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
			font-size: var(--font-size-small);
			color: var(--neutral-900);

			.green-dot {
				width: var(--spacing-sm);
				height: var(--spacing-sm);
				border-radius: var(--border-radius-full);
				background-color: var(--primary-green);
				flex-shrink: 0;
			}
		}
	}

	.cta {
		margin-top: var(--spacing-md);
		text-transform: uppercase;
		box-shadow: var(--shadow-base);
	}
}

.broker-card.compact {
	container-type: inline-size;
	container-name: broker-card;
	display: block;
	padding: 0;
	max-width: none;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	overflow: hidden;
	box-shadow: none;

	&.flush {
		border: none;
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
		border-radius: 0;
	}

	.top {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm) var(--spacing-md);
		background: var(--neutral-50);

		.avatar {
			position: relative;
			width: var(--space-56);
			height: var(--space-56);
			border-radius: var(--border-radius-full);
			overflow: hidden;
			flex-shrink: 0;

			img {
				width: 100%;
				height: 100%;
				object-fit: contain;
				object-position: 70% center;
				transform: scale(1.1);
				opacity: 0;
				transition: opacity 0.15s ease;

				&.loaded {
					opacity: 1;
				}
			}
		}

		.info {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs) var(--spacing-sm);
			flex: 1 1 auto;
			min-width: 0;

			@container broker-card (max-width: 320px) {
				flex-direction: column;
				align-items: flex-start;

				.cta {
					align-self: flex-start;
				}
			}
		}

		.text {
			flex: 1 1 auto;
			min-width: 0;
			font-family: var(--font-family-primary);
		}

		.name {
			font-size: var(--font-size-base);
			font-weight: var(--font-weight-semibold);
			color: var(--neutral-900);
			margin: 0;
		}

		.title {
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-regular);
			color: var(--neutral-600);
			margin-top: var(--spacing-xxs);
		}

		.cta {
			flex-shrink: 1;
			align-self: center;
			margin-top: 0;
			min-width: 0;
			padding: var(--spacing-xxs) var(--spacing-sm);
			text-transform: uppercase;
			box-shadow: none;
		}
	}

	.rows {
		.row {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
			width: 100%;
			padding: var(--spacing-sm) var(--spacing-md);
			border: none;
			border-top: var(--border-width-thin) solid var(--neutral-200);
			border-radius: 0;
			background: var(--neutral-0);
			font-size: var(--font-size-small);
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			text-align: left;
			cursor: pointer;
			transition: color 0.2s ease;

			.app-icon {
				flex-shrink: 0;
				color: var(--neutral-500);
				transition: color 0.2s ease;
			}

			&:hover {
				color: var(--primary-green);

				.app-icon {
					color: var(--primary-green);
				}
			}

			&.copied {
				color: var(--primary-green);
				font-weight: var(--font-weight-medium);

				.app-icon {
					color: var(--primary-green);
				}
			}
		}
	}
}

.avatar-skeleton {
	position: absolute;
	inset: 0;
	background: linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-50) 37%, var(--neutral-100) 63%);
	background-size: 400% 100%;
	animation: avatar-skeleton-shimmer 1.4s ease infinite;
}

@keyframes avatar-skeleton-shimmer {
	0% {
		background-position: 100% 0;
	}

	100% {
		background-position: 0 0;
	}
}

.contact-menu {
	display: flex;
	flex-direction: column;
	width: 160px;
	padding: var(--spacing-xs);
	background-color: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-sm);
	box-shadow: var(--shadow-md);
	z-index: 1000;

	.item {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm);
		border: none;
		border-radius: var(--border-radius-sm);
		background: none;
		width: 100%;
		text-align: left;
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		cursor: pointer;
		transition: color 0.2s ease;

		.app-icon {
			flex-shrink: 0;
			color: var(--neutral-500);
			transition: color 0.2s ease;
		}

		&:hover {
			color: var(--primary-green);

			.app-icon {
				color: var(--primary-green);
			}
		}
	}
}
</style>
